import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import Breadcrumbs from 'components/breadcrumbs';
import CheckboxSelectItem from 'components/checkbox-select-item';
import DatePicker from 'components/date-picker';
import Dialog from 'components/dialog';
import Divider from 'components/divider';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';

import Paper from 'components/paper';
import Select from 'components/select';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs, { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { Search } from 'lucide-react';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import _, { Dictionary } from 'underscore';
import { clearState } from 'utils/storage';
import { deleteRegistrationLog, getRegistrationLog, registrationToPonds } from 'views/production-systems/api/productionSystems';
import SelectFarm from 'views/production-systems/components/select-farm';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import ListFarms from 'views/production-systems/features/farm-overview/components/list-farms';
import EditAerators from 'views/production-systems/features/registration-log/components/edit-aerator';
import EditFeeding from 'views/production-systems/features/registration-log/components/edit-feeding';
import EditHarvest from 'views/production-systems/features/registration-log/components/edit-harvest';
import EditSampling from 'views/production-systems/features/registration-log/components/edit-sampling';
import EditStocking from 'views/production-systems/features/registration-log/components/edit-stocking';
import EditTransfer from 'views/production-systems/features/registration-log/components/edit-transfer';
import EditTreatment from 'views/production-systems/features/registration-log/components/edit-treatment';
import EditWaterQuality from 'views/production-systems/features/registration-log/components/edit-water-quality';
import { RegistrationType } from 'views/production-systems/features/registration-log/types/registration-type.enum';
import { Registration } from 'views/production-systems/features/registration-log/types/registration.type';
import ProductionSystemsTabHeader from '../../components/tab-header';
import EditFeeder from 'views/production-systems/features/registration-log/components/edit-feeder';

type RegistrationLogProps = {};

type ParamTypes = {
  globalSiteId: string;
};

interface RenderEditProps {
  handleDialogClose: () => void;
  registration: Registration;
}

const RenderEdit = ({ registration, handleDialogClose }: RenderEditProps) => {
  switch (registration.actionType) {
    case RegistrationType.Feeding:
      return (
        <EditFeeding
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    case RegistrationType.WaterQualitySample:
      return (
        <EditWaterQuality
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    case RegistrationType.Stocking:
      return (
        <EditStocking
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    case RegistrationType.Harvest:
      return (
        <EditHarvest
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    case RegistrationType.MeanWeightSample:
      return (
        <EditSampling
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    case RegistrationType.Transfer:
      return (
        <EditTransfer
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    case RegistrationType.Treatment:
      return (
        <EditTreatment
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    case RegistrationType.Aerators:
      return (
        <EditAerators
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    case RegistrationType.Feeders:
      return (
        <EditFeeder
          logId={registration.id}
          eventNr={registration.eventNr}
          canEdit={registration.canEdit}
          handleClose={handleDialogClose}
          dateCreated={dayjs(registration.dateCreated)}
        />
      );
    default:
      return null;
  }
};

const PAGE_NAME_ID = 'productionSystems.registrationLog';

const RegistrationLogOverview: React.FC<RegistrationLogProps> = ({ }) => {
  const params = useParams<ParamTypes>();
  const { account } = useAccountState();
  const intl = useIntl();
  const theme = useTheme();
  const { globalSiteId, lockedPonds, releasePonds, registrationLogDate, changeGlobalSiteId, lockPonds } = usePondsState();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();

  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const addRegistrationDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const removeDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const defaultFromDate = dayjs().subtract(1, 'months');

  //#region STATES

  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [toDate, setToDate] = useState<Dayjs>(registrationLogDate);
  const [fromDate, setFromDate] = useState<Dayjs>(defaultFromDate);
  const [actionTypes, setActionTypes] = useState<string[]>(Object.values(RegistrationType));
  const [showUserRegistrations, setShowUserRegistrations] = useState<boolean>(false);
  const [hideEmptyDays, setHideEmptyDays] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  //#endregion STATES

  //#region  FETCH DATA

  const { data: registrationData, isFetching } = useGet(
    ['getRegistrations', params.globalSiteId, fromDate.format('YYYY-MM-DD'), toDate.format('YYYY-MM-DD')],
    ({ queryKey }) => getRegistrationLog(String(queryKey[1]), String(queryKey[2]), String(queryKey[3])),
    {
      enabled: !!params.globalSiteId,
    }
  );

  useEffect(() => {
    if (!registrationData) return;

    let regs = _.sortBy(registrationData, (registration) => dayjs(registration.createdBy).format('L'));
    setRegistrations(regs);
  }, [registrationData]);

  //#endregion FETCH DATA

  //DELETE
  const deleteRegistrationItem = usePost<AxiosResponse, number>(
    (request) => deleteRegistrationLog(params.globalSiteId, request),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      removeDialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const handleDialogClose = () => {
    if (!!lockedPonds?.granted.length) releasePonds(lockedPonds.granted);
    dialogRef.current?.setOpen(false);
    addRegistrationDialogRef.current?.setOpen(false);
  };

  const handleDeleteRegistration = async (data: Registration) => {
    const pondIds = await registrationToPonds(globalSiteId, data);
    lockPonds(pondIds);
    if (lockedPonds.notGranted.length === 0) {
      deleteRegistrationItem.mutate(data.id, {
        onSuccess: () => releasePonds(pondIds),
      });
    }
  };

  const handleFromDateChange = useCallback((date: Dayjs) => {
    setFromDate(date);
  }, []);

  const handleToDateChange = useCallback((date: Dayjs) => {
    setToDate(date);
  }, []);

  // #region RENDERS

  const renderRegistrations = useCallback(() => {
    let rows: Dictionary<Registration[]> = {};

    rows = _.chain(registrations)
      .filter((registration) => String(registration.id).includes(searchValue))
      .filter((registration) => (!showUserRegistrations ? true : registration.createdBy === account.currentUser?.email))
      .filter((registration) => (hideEmptyDays ? true : registration.dateCreated))
      .filter((registration) => (!actionTypes ? true : actionTypes.includes(registration.actionType)))
      .sortBy((r) => {
        const n = r.eventNr ?? Number.NEGATIVE_INFINITY;
        return -n;
      })
      .sortBy((registrations) => (registrations.dateCreated ? -dayjs(registrations.dateCreated).valueOf() : Number.NEGATIVE_INFINITY))
      .groupBy((registration) => dayjs(registration.dateCreated).format('MMMM YYYY'))
      .value();

    return rows;
  }, [registrations, showUserRegistrations, hideEmptyDays, actionTypes, searchValue]);

  const options: HeadCell[] = useMemo(() => {
    return [
      { id: 'registrationDate', align: 'center', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.registrationDate` }) },
      {
        id: 'id',
        align: 'right',
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.logID` }),
      },
      {
        id: 'eventNumber',
        align: 'right',
        sx: { whiteSpace: 'nowrap' },
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.eventNumber` }),
      },

      { id: 'actionType', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.registrationType` }) },
      { id: 'userId', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.email` }) },
      {
        id: 'actions',
        label: '',
        autoWidth: true,
      },
    ];
  }, [intl]);

  // #endregion RENDERS

  useEffect(() => {
    if (params.globalSiteId) {
      changeGlobalSiteId(params.globalSiteId);
    } else {
      changeGlobalSiteId('');
    }
  }, [params.globalSiteId]);

  return (
    <>
      <ProductionSystemsTabHeader
        utils={
          <Breadcrumbs
            crumbs={
              !!params.globalSiteId
                ? [
                  {
                    url: params.globalSiteId,
                    text: account.company?.addressAccess.find((a) => a.id === params.globalSiteId)?.name || '',
                  },
                ]
                : undefined
            }
          />
        }
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingBottom: 4,
        }}>
        <SelectFarm
          selectedId={!!params.globalSiteId ? params.globalSiteId : ''}
          onChange={() => clearState({ key: 'pondFilters' })}
          uri={'/production-systems/registration-log'}
        />
      </Box>
      {!!params.globalSiteId ? (
        <Paper transparent>
          <FilterSidebar
            showClearFilter={
              actionTypes.length < Object.values(RegistrationType).length ||
              !fromDate.isSame(defaultFromDate, 'day') ||
              !toDate.isSame(registrationLogDate, 'day') ||
              !!searchValue ||
              showUserRegistrations ||
              hideEmptyDays
            }
            clearFilters={() => {
              setFromDate(defaultFromDate);
              setToDate(registrationLogDate);
              setShowUserRegistrations(false);
              setHideEmptyDays(false);
              setActionTypes(Object.values(RegistrationType));
              setSearchValue('');
            }}>
            <Box display={'flex'} gap={4} flexDirection={isMobile ? 'column' : 'row'} width={'100%'} justifyContent={'flex-end'}>
              <DatePicker
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.fromDate` })}
                value={fromDate}
                onChange={handleFromDateChange}
                maxDate={toDate}
                format={'DD/MM/YYYY'}
              />
              <DatePicker
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.toDate` })}
                value={toDate}
                minDate={fromDate}
                onChange={handleToDateChange}
                disableFuture
                format={'DD/MM/YYYY'}
              />
              <Select
                multiple
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.registrationType` })}
                value={actionTypes}
                checkAll={{
                  value: actionTypes.length === Object.keys(RegistrationType).length,
                  onChange: () =>
                    setActionTypes((prev) =>
                      prev.length === Object.keys(RegistrationType).length ? [] : Object.values(RegistrationType)
                    ),
                }}
                onChange={(event) => {
                  setActionTypes(event.target.value as string[]);
                }}>
                {Object.entries(RegistrationType)
                  .filter((type) => !isNaN(Number(type[0])) === false)
                  ?.map((type) => {
                    return (
                      <CheckboxSelectItem key={type[0]} value={type[1]} checked={actionTypes.includes(type[1])}>
                        {<FormattedMessage id={`${PAGE_NAME_ID}.registrationTypes.${type[1]}`} />}
                      </CheckboxSelectItem>
                    );
                  })}
              </Select>
              <TextField
                label={intl.formatMessage({ id: 'general.search' })}
                value={searchValue}
                onChange={(e) => setSearchValue(String(e.target.value))}
                endAdornment={<Search />}
              />
            </Box>
            <Box display={'flex'} gap={4}>
              <Switch
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showUserRegistrations` })}
                value={showUserRegistrations}
                onChange={() => setShowUserRegistrations((prev) => !prev)}
              />
              <Switch
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.hideEmptyDays` })}
                value={hideEmptyDays}
                onChange={() => setHideEmptyDays((prev) => !prev)}
              />
            </Box>
          </FilterSidebar>

          {isFetching ? (
            <LoadingSpinner />
          ) : registrations.length === 0 ? (
            <Typography sx={{ color: 'white' }}>
              <FormattedMessage id="productionSystems.registrationLog.notFoundMessage" />
            </Typography>
          ) : (
            Object.keys(renderRegistrations()).map((month, idx) => {
              let registrationGroup = renderRegistrations()[month];

              return (
                <Box key={idx}>
                  <Box color={'white'}>
                    <Typography textTransform={'capitalize'}>{month}</Typography>
                    <Divider
                      sx={{
                        margin: theme.spacing(2, 0),
                      }}
                    />
                  </Box>
                  <List
                    sx={{
                      height: registrationGroup.length > 10 ? 600 : '100%',
                    }}
                    noResult={{
                      show: !registrationGroup.length,
                      text: intl.formatMessage({ id: 'general.noResults' }),
                    }}
                    options={options}>
                    {registrationGroup.map((registration) => (
                      <ListRow key={registration.id}>
                        <ListCell align="center">{dayjs(registration.dateCreated).format('DD/MM/YYYY')}</ListCell>
                        <ListCell align="right">{registration.id}</ListCell>
                        <ListCell align="right">{registration.eventNr}</ListCell>
                        <ListCell align="left">
                          {<FormattedMessage id={`${PAGE_NAME_ID}.registrationTypes.${registration.actionType}`} />}
                        </ListCell>
                        <ListCell align="left">{registration.createdBy}</ListCell>
                        <ListCell>
                          <Box
                            display="flex"
                            pt={isMobile ? 4 : 0}
                            width={'100%'}
                            justifyContent={{ xs: 'space-between', md: 'space-around' }}
                            alignItems="center"
                            gap={2}>
                            <Dialog
                              maxWidth="xs"
                              ref={removeDialogRef}
                              title={intl.formatMessage({ id: 'dialog.delete.title' })}
                              button={
                                <Button size="small" disabled={!registration.canEdit}>
                                  <FormattedMessage id="general.delete" />
                                </Button>
                              }>
                              <Box display={'flex'} justifyContent={'flex-end'}>
                                <Button
                                  type="submit"
                                  onClick={() => handleDeleteRegistration(registration)}
                                  disabled={deleteRegistrationItem.isPending}
                                  startIcon={deleteRegistrationItem.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
                                  <FormattedMessage id="general.delete" />
                                </Button>
                              </Box>
                            </Dialog>

                            <Dialog
                              maxWidth="xl"
                              ref={addRegistrationDialogRef}
                              onClose={handleDialogClose}
                              title={`${intl.formatMessage({ id: `${PAGE_NAME_ID}.registrationTypes.${registration.actionType}` })} ${registration.id
                                }`}
                              button={
                                registration.canEdit ? (
                                  <Button size="small" disabled={!registration.canEdit}>
                                    <FormattedMessage id="general.edit" values={{ type: '' }} />
                                  </Button>
                                ) : (
                                  <Button size="small">
                                    <FormattedMessage id="general.view" />
                                  </Button>
                                )
                              }>
                              <RenderEdit registration={registration} handleDialogClose={handleDialogClose} />
                            </Dialog>
                          </Box>
                        </ListCell>
                      </ListRow>
                    ))}
                  </List>
                </Box>
              );
            })
          )}
        </Paper>
      ) : null}
    </>
  );
};

const RegistrationLog = () => {
  const params = useParams<{ globalSiteId: string }>();

  return !!params.globalSiteId ? <RegistrationLogOverview /> : <ListFarms uri={'/production-systems/registration-log'} />;
};

export default React.memo(RegistrationLog);
